import { Global } from '@emotion/react';
import { observer } from 'mobx-react-lite';
import QueryString from 'query-string';
import { Suspense, useEffect, useMemo } from 'react';
import { Outlet, useLocation, useMatches } from 'react-router';

import { CookiesTester } from '../../components/BrowserSupport/CookiesTester/CookiesTester';
import { DeepLinkUrlSync } from '../../components/DeepLinking/DeepLinkUrlSync';
import { NewReleaseNotification } from '../../components/NewReleaseNotification/NewReleaseNotification';
import {
	fetchDeployedReleaseIdOrThrow,
	LOCAL_RELEASE_ID,
} from '../../components/NewReleaseNotification/newReleaseNotification.utils';
import { NewReleaseNotificationModel } from '../../components/NewReleaseNotification/NewReleaseNotificationModel';
import { getOrCreateFeatureFlag } from '../../config/FeatureFlag';
import { NotificationProvider } from '../../contexts/Notification.context';
import { globalStyleWithCssVar } from '../../theme/globalStyle';
import { reloadPage } from '../../utilities/location';
import { isScreenLayoutEmbeddedOrOverlay } from '../../utilities/screenLayout';
import { getOrCreateAnalytics } from '../../utils/analytics';
import { NavinaDataProvider, useNavinaData } from '../../utils/useNavinaData';
import { hasPageHandle } from '../pageHandle.utils';
import { SpinnerLayout } from './SpinnerLayout';

function reloadPageOnRequest(): void {
	reloadPage({ preventLoop: false });
}

export const RootLayout = observer(function RootLayout() {
	const location = useLocation();
	const analytics = getOrCreateAnalytics();
	const { screenLayout } = useNavinaData();
	const matches = useMatches();

	const maybeDeepestMatchWithPageHandle = matches.findLast(hasPageHandle);
	const maybeCurrentPage = maybeDeepestMatchWithPageHandle?.handle.page ?? null;

	const featureFlag = getOrCreateFeatureFlag();
	const { flagsAreInitialized, isNewReleaseNotificationEnabled } = featureFlag;

	const newReleaseNotificationModel = useMemo(
		() =>
			NewReleaseNotificationModel.create({
				runningReleaseId: __RELEASE_ID__,
				getDeployedReleaseId: fetchDeployedReleaseIdOrThrow,
				reloadPage: reloadPageOnRequest,
			}),
		[],
	);
	const shouldWatchForNewRelease = isNewReleaseNotificationEnabled && __RELEASE_ID__ !== LOCAL_RELEASE_ID;
	const isNewReleaseNotificationCompact = isScreenLayoutEmbeddedOrOverlay(screenLayout);

	useEffect((): void => {
		const parsedQueries = QueryString.parse(globalThis.location.search);

		const src = Array.isArray(parsedQueries.src) ? parsedQueries.src[0] : parsedQueries.src;
		const openedAs = Array.isArray(parsedQueries.openedAs) ? parsedQueries.openedAs[0] : parsedQueries.openedAs;

		analytics.setAnalyticTrack({
			fullScreen: !globalThis.location.href.includes('overlay'),
			...(src && { src }),
			...(openedAs && { openedAs }),
		});
	}, [location]);

	useEffect(
		function updateFeatureFlagContext(): void {
			if (!flagsAreInitialized) {
				return;
			}

			featureFlag.extendUserContext({
				screen_layout: screenLayout,
				page: maybeCurrentPage,
			});
		},
		[screenLayout, maybeCurrentPage, flagsAreInitialized],
	);

	useEffect(
		function watchForNewRelease(): VoidFunction | undefined {
			if (!shouldWatchForNewRelease) {
				return undefined;
			}

			return newReleaseNotificationModel.startWatching();
		},
		[shouldWatchForNewRelease],
	);

	return (
		<>
			<DeepLinkUrlSync />
			<CookiesTester />
			<Global styles={globalStyleWithCssVar} />
			<NavinaDataProvider>
				<NotificationProvider>
					<Suspense fallback={<SpinnerLayout keepSpinnerVisible />}>
						<Outlet />
					</Suspense>
					{shouldWatchForNewRelease && (
						<NewReleaseNotification {...{ newReleaseNotificationModel, isCompact: isNewReleaseNotificationCompact }} />
					)}
				</NotificationProvider>
			</NavinaDataProvider>
		</>
	);
});
